import { useCallback, useMemo, useRef } from 'react'
import { useMutation, useQuery } from 'apollo-client'
import localStorage from 'local-storage'
import { openNotification } from 'notifications'
import { openModal } from 'modals'
import { track } from 'analytics'
import {
  CancelledPromiseError,
  constants,
  formatPrice,
  formatProductLabelsForAnalytics,
  GraphQLError,
  UserError,
} from 'helpers'
import { useUser } from 'modules/user'
import { useCookieCoupon, useInquireSubscriptionCoupon } from 'modules/coupon'

import cartQuery, { type CartVariables } from './graph/cart.graphql'
import cartModifyQuery, { type CartModifyVariables } from './graph/cartModify.graphql'
import cartDeleteQuery, { type CartDeleteVariables } from './graph/cartDelete.graphql'

import modifyCart from './modifyCart'


type OpenAddedProductNotificationProps = {
  count?: number
  name: string
  brand: string
  image: string
  rebrandImage: string
}

const openAddedProductNotification = ({ count = 1, name, brand, image, rebrandImage }: OpenAddedProductNotificationProps) => {
  openNotification('productAdded', {
    to: 'cart',
    count,
    name,
    brand,
    image,
    rebrandImage,
  })
}

const useCartInput = () => {
  const { user, isLoggedIn, isFetching: isUserFetching } = useUser()

  // address
  const localStorageAddress = __CLIENT__ ? localStorage.getItem<{ country: string }>(constants.localStorageNames.shippingFormValues) : null
  const country = user?.addressInfo?.shipping?.country || localStorageAddress?.country

  const address = useMemo(() => {
    // we actually need only country for shipping
    if (country) {
      return {
        country,
      }
    }

    return null
  }, [ country ])

  // coupon
  // 1. the main source of truth is the applied coupon. The only way to fetch it is purchaseInquire
  const { inquireSubscriptionCouponCode, isFetching: isAppliedCouponFetching } = useInquireSubscriptionCoupon({
    skip: !isLoggedIn, // we can't fetch it for anonymous users
  })

  // 2. the second source of truth is coupons from cookies. For users whose goes from landings or without any coupon applied
  const { cookieCouponCode } = useCookieCoupon()
  const couponCode = inquireSubscriptionCouponCode || cookieCouponCode

  const cartInputRef = useRef(null)
  cartInputRef.current = { address, couponCode }

  return {
    cartInputRef,
    isCartInputFetching: isUserFetching || isAppliedCouponFetching,
  }
}

export const useCart = (input?: CartVariables['input']) => {
  const { cartInputRef, isCartInputFetching } = useCartInput()

  const { data, isFetching } = useQuery(cartQuery, {
    fetchPolicy: 'cache-first',
    ssr: false,
    skip: isCartInputFetching,
    variables: {
      input: {
        ...cartInputRef.current,
        purchaseLevel: 'ALL',
        ...input,
      },
    },
  })

  const cart = useMemo(() => {
    const cartData = data?.currentUser?.data?.cart

    if (!cartData) {
      return null
    }

    return modifyCart(cartData)
  }, [ data ])

  return {
    cart,
    isFetching: isCartInputFetching || isFetching,
  }
}

// this hook handles business logic when user add something to the cart
export const useCartAddProduct = () => {
  const [ modifyCart, state ] = useCartModify()
  const apolloClient = state.client

  const submit = useCallback<CartModule.AddProduct>(async (input, params = {}) => {
    const {
      product: {
        tradingItemId,
        quantity = 1,
        offer,
        name,
        brand,
        image,
        rebrandImage,
        productId,
        isSubscribedToStockNotification,
      },
      metadata,
    } = input

    const { withNotification = true, withErrorModals = true } = params

    // ATTN works only with ecommerce products and ALL purchase level
    const cartInfo = apolloClient.cache.readQuery({
      query: cartQuery,
      variables: {
        input: {
          purchaseLevel: 'ALL',
        },
      },
    })

    const productInfo = cartInfo?.currentUser?.data?.cart?.products?.find((item) => item.id === tradingItemId)

    // backend doesn't add items to the cart, it modifies the cart, so we need to count the amount of products manually
    const currentQuantity = productInfo?.quantity || 0

    const { data: newCartInfo, error } = await modifyCart({
      productItems: [
        {
          productLineItemId: tradingItemId,
          quantity: currentQuantity + quantity,
          offer,
        },
      ],
      metadata,
    })

    const addedProductInfo = newCartInfo?.products?.find((item) => item.id === tradingItemId)

    if (error && withErrorModals) {
      if (error.__typename === 'CartError') {
        if (error.cartErrorCode === 'OUT_OF_STOCK') {
          openModal('outOfStockModal', {
            productId,
            isSubscribedToStockNotification,
          })
          throw new CancelledPromiseError()
        }
      }

      throw new UserError(error, error?.message)
    }

    if (withNotification) {
      openAddedProductNotification({ name, brand, image, rebrandImage })
    }

    const productLabels = formatProductLabelsForAnalytics(addedProductInfo?.product)

    const productCategory = addedProductInfo?.product.category.toLowerCase()
    // TODO: Remove after back implementation of metadata storage — added on 07–07–2022 by algeas
    const storedMetadata = localStorage.getItem(constants.localStorageNames.lastAddedToCartProductMetadata)
    const page = metadata.pageType === 'Product' ? `The Shop ${productCategory} product` : metadata.page
    const hasTheEditProducts = storedMetadata?.hasTheEditProducts || addedProductInfo?.product.theEdit

    const metadataToStore = {
      ...metadata,
      page,
      hasTheEditProducts,
    }

    track('Add to cart front', {
      page,
      productLabels,
      placement: metadata.placement,
      productPrice: Number(formatPrice(addedProductInfo?.price, { isCents: true, withCurrency: false })),
      productId: addedProductInfo?.product.id,
      productGender: addedProductInfo?.product.gender,
      productVolume: addedProductInfo?.product ? `${addedProductInfo?.product.volume} ${addedProductInfo?.product.unit}` : undefined,
      productFullName: addedProductInfo?.product.fullName,
      productCategory: addedProductInfo?.product.category,
      productBrand: addedProductInfo?.product.brandInfo.name,
      isTheEdit: addedProductInfo?.product?.theEdit,
    })

    localStorage.setItem(constants.localStorageNames.lastAddedToCartProductMetadata, metadataToStore)
  }, [ modifyCart ])

  return [
    submit,
    state,
  ] as const
}

export type CartModifyInput = CartModifyVariables['input']

export const useCartModify = () => {
  const { cartInputRef } = useCartInput()
  const [ mutate, { isFetching, client } ] = useMutation(cartModifyQuery)

  const submit = useCallback(async (input: CartModifyInput) => {
    const result = await mutate({
      variables: {
        input: {
          ...input,
          options: {
            ...cartInputRef.current,
            purchaseLevel: 'ALL',
            ...input.options,
          },
        },
      },
      // manual cache update
      fetchPolicy: 'no-cache',
      update: (cache, result, options) => {
        if (result.data.cartModify.data) {
          cache.writeQuery({
            query: cartQuery,
            variables: { input: options.variables.input.options },
            data: {
              currentUser: {
                __typename: 'UserPayload',
                data: {
                  __typename: 'UserData',
                  cart: result.data.cartModify.data.cart,
                },
                error: null,
              },
            },
          })
        }
      },
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { data, error } = result.data.cartModify

    return {
      error,
      data: !error ? modifyCart(data.cart) : null,
    }
  }, [ mutate, cartInputRef ])

  return [
    submit,
    {
      client,
      isFetching,
    },
  ] as const
}

export type CartDeleteInput = CartDeleteVariables['input']

export const useCartDelete = () => {
  const { cartInputRef } = useCartInput()
  const [ mutate, { isFetching } ] = useMutation(cartDeleteQuery)

  const submit = useCallback(async (input: CartDeleteInput) => {
    const result = await mutate({
      variables: {
        input: {
          ...input,
          options: {
            ...cartInputRef.current,
            purchaseLevel: 'ALL',
            ...input.options,
          },
        },
      },
      update: (cache, result, options) => {
        cache.writeQuery({
          query: cartQuery,
          variables: { input: options.variables.input.options },
          data: {
            currentUser: {
              __typename: 'UserPayload',
              data: {
                __typename: 'UserData',
                cart: result.data.cartDelete.data.cart,
              },
              error: null,
            },
          },
        })
      },
      // manual cache update
      fetchPolicy: 'no-cache',
    })

    if (result.errors) {
      throw new GraphQLError(result.errors)
    }

    const { data, error } = result.data.cartDelete

    if (error) {
      throw new UserError(error, error.message)
    }

    return modifyCart(data.cart)
  }, [ mutate, cartInputRef ])

  return [
    submit,
    {
      isFetching,
    },
  ] as const
}
